import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, resolveImageUrl } from '@/lib/axios';
import { ProductCategory, ProductVariant } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Upload,
  Plus,
  Trash2,
  Link as LinkIcon,
  Star,
  Layers,
} from 'lucide-react';

interface VariantDraftItem {
  id: string;
  type: string; // e.g. "Color", "Base", "Tamaño"
  value: string; // e.g. "Blanco Cálido", "Base Plana"
  priceAdjustment: number;
  imageUrl?: string;
}

export default function AddProductPageAdmin() {
  const navigate = useNavigate();

  // Categorías
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('');
  const [isNewCategoryModalOpen, setIsNewCategoryModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  // Form State
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [barcode, setBarcode] = useState('');
  const [description, setDescription] = useState('');

  // Pricing
  const [basePrice, setBasePrice] = useState<number | ''>('');
  const [discountedPrice, setDiscountedPrice] = useState<number | ''>('');
  const [chargeTax, setChargeTax] = useState(false);
  const [inStock, setInStock] = useState(true);

  // Status
  const [status, setStatus] = useState<'Active' | 'Draft' | 'Archived'>('Active');

  // Parámetros 3D
  const [weightGrams, setWeightGrams] = useState<number | ''>(120);
  const [printTimeHours, setPrintTimeHours] = useState<number | ''>(4.5);
  const [estimatedDeliveryDays, setEstimatedDeliveryDays] = useState<number | ''>(3);

  // Images
  const [images, setImages] = useState<string[]>([]);
  const [isUrlModalOpen, setIsUrlModalOpen] = useState(false);
  const [urlInput, setUrlInput] = useState('');

  // Variants
  const [variants, setVariants] = useState<VariantDraftItem[]>([
    { id: '1', type: 'Color', value: 'Blanco Cálido', priceAdjustment: 0, imageUrl: '' },
    { id: '2', type: 'Base', value: 'Base Plana', priceAdjustment: 0, imageUrl: '' },
  ]);

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const res = await api.get<ProductCategory[]>('/product-categories');
      if (Array.isArray(res.data) && res.data.length > 0) {
        setCategories(res.data);
        setSelectedCategoryId(String(res.data[0].id));
      }
    } catch {
      // fallback
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const slug = newCategoryName.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-');
      const res = await api.post('/product-categories', {
        name: newCategoryName.trim(),
        slug: slug,
        sortOrder: categories.length + 1,
        isActive: true,
      });
      toast.success('Categoría creada');
      setIsNewCategoryModalOpen(false);
      setNewCategoryName('');
      loadCategories();
      if (res.data?.id) {
        setSelectedCategoryId(String(res.data.id));
      }
    } catch {
      toast.error('Error al crear la categoría');
    }
  };

  const handleAddImageUrl = () => {
    if (!urlInput.trim()) return;
    setImages((prev) => [...prev, urlInput.trim()]);
    setUrlInput('');
    setIsUrlModalOpen(false);
    toast.success('Imagen agregada');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (loadEvent) => {
        const result = loadEvent.target?.result as string;
        if (result) {
          setImages((prev) => [...prev, result]);
        }
      };
      reader.readAsDataURL(file);
    });

    toast.success(`${files.length} imagen(es) cargada(s)`);
  };

  const handleRemoveImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSetMainImage = (index: number) => {
    setImages((prev) => {
      const copy = [...prev];
      const [item] = copy.splice(index, 1);
      return [item, ...copy];
    });
    toast.success('Imagen establecida como principal');
  };

  const handleAddVariantRow = () => {
    const newItem: VariantDraftItem = {
      id: Date.now().toString(),
      type: 'Color',
      value: '',
      priceAdjustment: 0,
      imageUrl: images[0] || '',
    };
    setVariants((prev) => [...prev, newItem]);
  };

  const handleUpdateVariantRow = (id: string, field: keyof VariantDraftItem, val: any) => {
    setVariants((prev) =>
      prev.map((v) => (v.id === id ? { ...v, [field]: val } : v))
    );
  };

  const handleRemoveVariantRow = (id: string) => {
    setVariants((prev) => prev.filter((v) => v.id !== id));
  };

  const handleSubmit = async (publishNow: boolean) => {
    if (!name.trim()) {
      toast.error('El nombre del producto es obligatorio');
      return;
    }
    if (!basePrice || Number(basePrice) <= 0) {
      toast.error('Ingresa un Precio Base válido');
      return;
    }

    try {
      setIsSaving(true);
      const generatedSlug = name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '');

      // Formatear variantes
      const formattedVariants: ProductVariant[] = variants
        .filter((v) => v.value.trim() !== '')
        .map((v) => ({
          id: v.id,
          name: v.value.trim(),
          type: v.type.toLowerCase() as any,
          priceAdjustment: Number(v.priceAdjustment) || 0,
          imageUrl: v.imageUrl || (images[0] || ''),
        }));

      const finalStatus = publishNow ? 'Active' : status;
      const isPublic = finalStatus === 'Active';
      const isOnSale = discountedPrice !== '' && Number(discountedPrice) > 0 && Number(discountedPrice) < Number(basePrice);

      const payload = {
        name: name.trim(),
        slug: generatedSlug,
        sku: sku.trim() || `ML-${Date.now().toString().slice(-6)}`,
        categoryId: selectedCategoryId ? Number(selectedCategoryId) : 1,
        salePrice: Number(basePrice),
        promotionalPrice: isOnSale ? Number(discountedPrice) : null,
        isOnSale: isOnSale,
        costPrice: Math.round(Number(basePrice) * 0.35), // Costo estimado 35%
        description: description.trim() || name.trim(),
        shortDescription: description ? description.slice(0, 100) : name.trim(),
        isPublic: isPublic,
        isActive: inStock,
        isFeatured: false,
        mainImageUrl: images[0] || '',
        images: JSON.stringify(images),
        variants: JSON.stringify(formattedVariants),
        weightGrams: weightGrams ? Number(weightGrams) : 120,
        printTimeHours: printTimeHours ? Number(printTimeHours) : 4.5,
        estimatedDeliveryDays: estimatedDeliveryDays ? Number(estimatedDeliveryDays) : 3,
        stockQuantity: inStock ? 50 : 0,
        manageStock: false,
        allowBackorder: true,
        sortOrder: 0,
      };

      const res = await api.post('/products', payload);
      toast.success(publishNow ? '¡Producto publicado con éxito!' : 'Borrador guardado');
      if (res.data?.id) {
        navigate(`/products-admin/${res.data.id}`);
      } else {
        navigate('/products-admin');
      }
    } catch (err: any) {
      toast.error('Error al guardar el producto');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16 font-sans">
      {/* Header (Back, Title, Discard, Save Draft, Publish) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border/60">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon" className="h-9 w-9 rounded-lg">
            <Link to="/products-admin">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Añadir Producto
            </h1>
            <p className="text-xs text-muted-foreground">
              Crea una nueva pieza de diseño 3D para el catálogo y tienda de Mila
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/products-admin')}
            disabled={isSaving}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Descartar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSubmit(false)}
            disabled={isSaving}
            className="text-xs font-medium"
          >
            Guardar Borrador
          </Button>
          <Button
            size="sm"
            onClick={() => handleSubmit(true)}
            disabled={isSaving}
            className="text-xs font-semibold bg-foreground text-background hover:bg-foreground/90 shadow-xs"
          >
            {isSaving ? 'Publicando...' : 'Publicar'}
          </Button>
        </div>
      </div>

      {/* Main Grid: Left (col-span-2), Right (col-span-1) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* ── LEFT COLUMN: Product Details, Images, Variants ── */}
        <div className="lg:col-span-2 space-y-6">
          {/* Card: Product Details */}
          <Card className="border-border/70 shadow-2xs">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold">Detalles del Producto</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="prod-name" className="text-xs font-medium">
                  Nombre <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="prod-name"
                  placeholder="ej. Lámpara Nube Nórdica Japandi"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-10 text-sm"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="prod-sku" className="text-xs font-medium">
                    SKU (Código Interno)
                  </Label>
                  <Input
                    id="prod-sku"
                    placeholder="ej. MLA-LP-01"
                    value={sku}
                    onChange={(e) => setSku(e.target.value)}
                    className="h-9 text-xs font-mono"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="prod-barcode" className="text-xs font-medium">
                    Código de Barras / EAN
                  </Label>
                  <Input
                    id="prod-barcode"
                    placeholder="ej. 779123456789"
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    className="h-9 text-xs font-mono"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="prod-desc" className="text-xs font-medium">
                  Descripción (Opcional)
                </Label>
                <textarea
                  id="prod-desc"
                  placeholder="Detalla las características estéticas, acabado y materiales de esta pieza..."
                  value={description}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                  rows={4}
                  className="w-full rounded-md border border-border bg-background p-2.5 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <p className="text-[11px] text-muted-foreground">
                  Escribe una descripción completa del producto para mayor visibilidad y claridad en la tienda.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Card: Product Images */}
          <Card className="border-border/70 shadow-2xs">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-bold">Imágenes del Producto</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsUrlModalOpen(true)}
                className="text-xs text-primary hover:underline h-8 px-2 gap-1"
              >
                <LinkIcon className="h-3 w-3" /> Añadir imagen desde URL
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Dropzone */}
              <label className="border-2 border-dashed border-border rounded-xl p-8 flex flex-col items-center justify-center text-center hover:bg-muted/30 transition-colors cursor-pointer group">
                <div className="p-3 rounded-full bg-muted/60 text-muted-foreground group-hover:text-primary transition-colors mb-3">
                  <Upload className="h-6 w-6" />
                </div>
                <p className="text-sm font-semibold text-foreground">
                  Arrastra tus imágenes aquí
                </p>
                <p className="text-xs text-muted-foreground mt-1 mb-3">
                  PNG o JPG (máx. 5MB)
                </p>
                <div className="inline-flex items-center px-3 py-1.5 rounded-lg border border-border bg-background text-xs font-medium shadow-2xs group-hover:border-primary/50 transition-colors">
                  Seleccionar imágenes
                </div>
                <input
                  type="file"
                  multiple
                  accept="image/png, image/jpeg, image/webp"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>

              {/* Gallery of Uploaded Images */}
              {images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                  {images.map((img, idx) => (
                    <div
                      key={idx}
                      className="group relative aspect-square rounded-lg border border-border overflow-hidden bg-muted/30"
                    >
                      <img
                        src={resolveImageUrl(img)}
                        alt={`Preview ${idx}`}
                        className="w-full h-full object-cover"
                      />
                      {idx === 0 && (
                        <Badge className="absolute top-1.5 left-1.5 text-[9px] bg-primary text-primary-foreground py-0 px-1.5 shadow-2xs">
                          Principal
                        </Badge>
                      )}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                        {idx !== 0 && (
                          <Button
                            size="icon"
                            variant="secondary"
                            className="h-7 w-7 rounded-md"
                            title="Establecer como principal"
                            onClick={() => handleSetMainImage(idx)}
                          >
                            <Star className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="destructive"
                          className="h-7 w-7 rounded-md"
                          title="Eliminar foto"
                          onClick={() => handleRemoveImage(idx)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Card: Variants (Options, Value, Price, Photo) */}
          <Card className="border-border/70 shadow-2xs">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base font-bold">Variantes</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Configura opciones de color, tipo de base o tamaño que conmutan la foto estilo MercadoLibre
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleAddVariantRow}
                className="gap-1.5 text-xs h-8"
              >
                <Plus className="h-3.5 w-3.5" /> Añadir Variante
              </Button>
            </CardHeader>
            <CardContent>
              {variants.length === 0 ? (
                <div className="text-center py-6 text-xs text-muted-foreground border border-dashed rounded-lg">
                  No hay variantes definidas. Haz click en &quot;Añadir Variante&quot; para crear opciones.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border/60 text-muted-foreground text-left">
                        <th className="pb-2 font-medium w-32">Opción</th>
                        <th className="pb-2 font-medium">Valor</th>
                        <th className="pb-2 font-medium w-28">Ajuste Precio</th>
                        <th className="pb-2 font-medium">Foto Asociada</th>
                        <th className="pb-2 font-medium w-12 text-center"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/40">
                      {variants.map((v) => (
                        <tr key={v.id} className="py-2">
                          <td className="py-2 pr-2">
                            <select
                              value={v.type}
                              onChange={(e) => handleUpdateVariantRow(v.id, 'type', e.target.value)}
                              className="w-full h-8 px-2 rounded-md border border-border bg-background text-xs"
                            >
                              <option value="Color">Color</option>
                              <option value="Base">Base</option>
                              <option value="Tamaño">Tamaño</option>
                              <option value="Material">Material</option>
                              <option value="Acabado">Acabado</option>
                            </select>
                          </td>
                          <td className="py-2 pr-2">
                            <Input
                              placeholder="ej. Base Plana o Blanco Hueso"
                              value={v.value}
                              onChange={(e) => handleUpdateVariantRow(v.id, 'value', e.target.value)}
                              className="h-8 text-xs"
                            />
                          </td>
                          <td className="py-2 pr-2">
                            <Input
                              type="number"
                              placeholder="0"
                              value={v.priceAdjustment}
                              onChange={(e) => handleUpdateVariantRow(v.id, 'priceAdjustment', Number(e.target.value))}
                              className="h-8 text-xs"
                            />
                          </td>
                          <td className="py-2 pr-2">
                            <select
                              value={v.imageUrl || ''}
                              onChange={(e) => handleUpdateVariantRow(v.id, 'imageUrl', e.target.value)}
                              className="w-full h-8 px-2 rounded-md border border-border bg-background text-xs"
                            >
                              <option value="">Foto Principal por Defecto</option>
                              {images.map((img, i) => (
                                <option key={i} value={img}>
                                  Foto #{i + 1}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="py-2 text-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-destructive"
                              onClick={() => handleRemoveVariantRow(v.id)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ── RIGHT COLUMN: Pricing, Status, Categories, 3D specs ── */}
        <div className="space-y-6">
          {/* Card: Pricing */}
          <Card className="border-border/70 shadow-2xs">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold">Precios</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="base-price" className="text-xs font-medium">
                  Precio Base ($ ARS) <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-xs text-muted-foreground font-semibold">$</span>
                  <Input
                    id="base-price"
                    type="number"
                    placeholder="25000"
                    value={basePrice}
                    onChange={(e) => setBasePrice(e.target.value === '' ? '' : Number(e.target.value))}
                    className="h-9 pl-7 text-xs font-semibold"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="disc-price" className="text-xs font-medium">
                  Precio con Descuento (Opcional)
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-xs text-muted-foreground font-semibold">$</span>
                  <Input
                    id="disc-price"
                    type="number"
                    placeholder="21900"
                    value={discountedPrice}
                    onChange={(e) => setDiscountedPrice(e.target.value === '' ? '' : Number(e.target.value))}
                    className="h-9 pl-7 text-xs"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="charge-tax"
                  checked={chargeTax}
                  onChange={(e) => setChargeTax(e.target.checked)}
                  className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                />
                <Label htmlFor="charge-tax" className="text-xs cursor-pointer font-normal">
                  Cobrar impuestos en este producto
                </Label>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-border/60">
                <div>
                  <Label htmlFor="in-stock" className="text-xs font-semibold block">
                    En stock
                  </Label>
                  <p className="text-[11px] text-muted-foreground">
                    Habilita la compra directa de este producto
                  </p>
                </div>
                <input
                  type="checkbox"
                  id="in-stock"
                  checked={inStock}
                  onChange={(e) => setInStock(e.target.checked)}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                />
              </div>
            </CardContent>
          </Card>

          {/* Card: Status */}
          <Card className="border-border/70 shadow-2xs">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold">Estado</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as any)}
                className="w-full h-9 px-3 rounded-lg border border-border bg-background text-xs font-medium focus:ring-1 focus:ring-primary"
              >
                <option value="Active">● Activo (Público en Tienda)</option>
                <option value="Draft">● Borrador (Oculto)</option>
                <option value="Archived">● Archivado</option>
              </select>
              <p className="text-[11px] text-muted-foreground">
                Define el estado de publicación del producto.
              </p>
            </CardContent>
          </Card>

          {/* Card: Categories */}
          <Card className="border-border/70 shadow-2xs">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold">Categorías</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Categoría Principal</Label>
                <div className="flex gap-2">
                  <select
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                    className="flex-1 h-9 px-3 rounded-lg border border-border bg-background text-xs font-medium focus:ring-1 focus:ring-primary"
                  >
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    title="Añadir nueva categoría"
                    onClick={() => setIsNewCategoryModalOpen(true)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Subcategoría</Label>
                <div className="flex gap-2">
                  <select
                    value={selectedSubCategory}
                    onChange={(e) => setSelectedSubCategory(e.target.value)}
                    className="flex-1 h-9 px-3 rounded-lg border border-border bg-background text-xs font-medium focus:ring-1 focus:ring-primary"
                  >
                    <option value="">Seleccionar subcategoría</option>
                    <option value="mesas">Lámparas de Mesa</option>
                    <option value="colgantes">Lámparas Colgantes</option>
                    <option value="jarrones">Jarrones & Floreros</option>
                    <option value="macetas">Macetas & Jardinería</option>
                    <option value="organizacion">Organización & Deco</option>
                  </select>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    title="Añadir subcategoría"
                    onClick={() => toast.info('Seleccionada subcategoría')}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card: Fabricación 3D (Taller Mila) */}
          <Card className="border-border/70 shadow-2xs bg-muted/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5" /> Métricas de Impresión 3D
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">Peso Filamento (g)</Label>
                  <Input
                    type="number"
                    value={weightGrams}
                    onChange={(e) => setWeightGrams(e.target.value === '' ? '' : Number(e.target.value))}
                    className="h-8 text-xs font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">Tiempo Impresión (h)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={printTimeHours}
                    onChange={(e) => setPrintTimeHours(e.target.value === '' ? '' : Number(e.target.value))}
                    className="h-8 text-xs font-mono"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Días Estimados de Entrega</Label>
                <Input
                  type="number"
                  value={estimatedDeliveryDays}
                  onChange={(e) => setEstimatedDeliveryDays(e.target.value === '' ? '' : Number(e.target.value))}
                  className="h-8 text-xs font-mono"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal: Añadir imagen desde URL */}
      <Dialog open={isUrlModalOpen} onOpenChange={setIsUrlModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">Añadir imagen desde URL</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="img-url" className="text-xs">
              URL directa de la imagen (HTTPS)
            </Label>
            <Input
              id="img-url"
              placeholder="https://ejemplo.com/fotos/lampara-mila.jpg"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              className="h-9 text-xs"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setIsUrlModalOpen(false)}>
              Cancelar
            </Button>
            <Button size="sm" onClick={handleAddImageUrl}>
              Agregar Imagen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Crear Nueva Categoría */}
      <Dialog open={isNewCategoryModalOpen} onOpenChange={setIsNewCategoryModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">Crear Nueva Categoría</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="new-cat-name" className="text-xs">
              Nombre de la Categoría
            </Label>
            <Input
              id="new-cat-name"
              placeholder="ej. Colección Wabi-Sabi"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              className="h-9 text-xs"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setIsNewCategoryModalOpen(false)}>
              Cancelar
            </Button>
            <Button size="sm" onClick={handleCreateCategory}>
              Crear Categoría
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
